import { Icon } from "@chakra-ui/react";
import { MdEuro } from "react-icons/md";
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { translate } from "../../../i18n";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { getDemoGrowth, getDemoRevenue, isDemoOrganization } from "../demoAnalyticsData";
import { AnalyticsFilterParams } from "../hooks/analyticsFilterParams";
import { useGetRevenue } from "../hooks/useGetRevenue";
import { computeGrowth } from "../utils/computeGrowth";
import { UNKNOWN_STAT_VALUE, isUnreadable, statPlaceholder } from "../utils/statValue";

type Props = {
    boxColor: string;
    brandColor: string;
    startDate: Date;
    endDate: Date;
    previousStartDate: Date | null;
    previousEndDate: Date | null;
    growthLabel?: string;
    filters?: AnalyticsFilterParams;
};

function formatCurrency(value: number): string {
    return `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export default function RevenueCard({ boxColor, brandColor, startDate, endDate, previousStartDate, previousEndDate, growthLabel, filters }: Props) {
    const { selectedLocation } = useSelectedLocation();
    const { selectedOrganization } = useSelectedOrganization();
    const isDemo = isDemoOrganization(selectedOrganization?.id);
    /**
     * `GET analytics/revenue` carries `@UseGuards(AdminAuthGuard)` — read from
     * `api/src/analytics/analytics.controller.ts` this run, not inherited — so a
     * stale dashboard token or an operator acting on an org they are no longer
     * an admin of is REFUSED, with no outage at all, on top of the ordinary
     * 5xx/network paths. Every one of those leaves `data === undefined`, which
     * this card used to render as `0,00 €`: a money figure, stated as fact, on
     * the screen an operator uses to decide whether the month was bad.
     */
    const { data: totalRevenue, isInitialLoading: isTotalRevenuePending, isError: totalError } = useGetRevenue(selectedLocation?.id, undefined, undefined, filters);
    const { data, isInitialLoading: isPeriodPending, isError: periodError } = useGetRevenue(selectedLocation?.id, startDate, endDate, filters);
    // Comparison read: feeds `computeGrowth` only, which returns undefined when
    // either side is undefined, so a failed read removes the growth chip rather
    // than mis-stating it. Allowlisted in `analytics-read-error-gate` with that
    // reason, and pinned there so a `?? 0` cannot be slipped in front of it.
    const { data: previousData } = useGetRevenue(
        selectedLocation?.id,
        previousStartDate ?? undefined,
        previousEndDate ?? undefined,
        filters,
    );

    const orgId = selectedOrganization?.id;
    const totalUnreadable = isUnreadable(totalError, totalRevenue);
    const periodUnreadable = isUnreadable(periodError, data);
    const demoTotal = isDemo ? getDemoRevenue(undefined, undefined, orgId) : 0;
    const demoPeriod = isDemo ? getDemoRevenue(startDate, endDate, orgId) : 0;
    /**
     * The last arm used to be a literal `'0 €'`, reached whenever the read had
     * no data and was not loading — which on `/analytics` means "no location is
     * selected", because the page renders this card whether one is or not and a
     * disabled query answers nothing forever (R100-1). A venue that has taken
     * nothing still says `0 €`: that comes from the `Boolean(totalRevenue)` arm
     * above, since the API answers `{ value: 0 }`, an object.
     */
    const totalValue = isDemo ? formatCurrency(demoTotal) : totalUnreadable ? UNKNOWN_STAT_VALUE : Boolean(totalRevenue) ? formatCurrency(totalRevenue?.value) : statPlaceholder(isTotalRevenuePending);
    const partialValue = isDemo ? formatCurrency(demoPeriod) : periodUnreadable ? UNKNOWN_STAT_VALUE : Boolean(data) ? formatCurrency(data?.value) : statPlaceholder(isPeriodPending);
    const growth = isDemo
        ? getDemoGrowth(demoPeriod, previousStartDate, previousEndDate, getDemoRevenue, orgId)
        : previousStartDate ? computeGrowth(data?.value, previousData?.value) : undefined;

    return (
        <MiniStatistics
            startContent={
                <IconBox
                    w='56px'
                    h='56px'
                    bg={boxColor}
                    icon={<Icon w='32px' h='32px' as={MdEuro} color={brandColor} />}
                />
            }
            name={translate('analytics.revenue')}
            value={`${partialValue} / ${totalValue}`}
            isError={!isDemo && (periodUnreadable || totalUnreadable)}
            growth={growth}
            growthLabel={growthLabel}
        />
    );
}
